<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <template v-if="!isLoading">
      <template v-if="riders.length === 0">
        <v-card class="card-container d-flex pa-6" elevation="0">
          <v-row>
            <v-col cols="12">
              <span class="title-text">จัดการพนักงานส่ง</span>
            </v-col>

            <v-col
              cols="12"
              class="d-flex flex-column align-center justify-center"
              style="min-height: 88vh"
            >
              <div class="d-flex flex-column align-center">
                <v-img>
                  <img
                    src="@/assets/not_exist_search.svg"
                    alt="no-data"
                    width="103"
                    height="103"
                  />
                </v-img>
                <span class="no-data-text mb-4"> ยังไม่มีรายการพนักงานส่ง </span>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </template>

      <template v-else>
        <v-card class="card-container pa-2" elevation="0">
          <v-card-title>
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex align-center justify-center justify-md-start"
              >
                <span class="title-text">จัดการพนักงานส่ง</span>
              </v-col>
            </v-row>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12">
                <RiderCard
                  :riders-data="riders"
                  :selected-card-index="selectedCardIndex"
                  @select-card="onSelectCard"
                />
              </v-col>

              <v-col cols="12">
                <Select
                  :search="search"
                  :selected-status="selectedStatus"
                  :date-range="dateRange"
                  @update:search="search = $event"
                  @update:selected-status="selectedStatus = $event"
                  @update:date-range="dateRange = $event"
                />
              </v-col>

              <v-col cols="12">
                <template v-if="filteredRiders.length > 0">
                  <Table
                    :riders="filteredRiders"
                    :user-role="$store.getters.role"
                    @view="openRiderDetail"
                    @toggle-suspend="toggleSuspendRider"
                    @approve="openApproveDialog"
                    @reject="openRejectDialog"
                  />
                </template>

                <template v-else>
                  <v-row>
                    <v-col
                      cols="12"
                      class="d-flex flex-column align-center justify-center"
                      style="min-height: 45vh"
                    >
                      <div class="d-flex flex-column align-center">
                        <v-img>
                          <img
                            src="@/assets/not_exist_search.svg"
                            alt="no-data"
                            width="103.8"
                            height="103.8"
                          />
                        </v-img>
                        <span class="no-data-text mb-4">
                          ไม่พบข้อมูลที่ค้นหา กรุณาลองใหม่อีกครั้ง
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </template>

    <ApproveDialog
      v-model="showApproveDialog"
      :rider="selectedRider"
      :saving="approving"
      @confirm="handleApprove"
    />

    <RejectDialog
      v-model="showRejectDialog"
      :rider="selectedRider"
      :saving="rejecting"
      @confirm="handleReject"
    />
  </v-container>
</template>

<script>
import RiderCard from "@/components/AdminRiderManagement/Card.vue";
import Select from "@/components/AdminRiderManagement/Select.vue";
import Table from "@/components/AdminRiderManagement/Table.vue";
import ApproveDialog from "@/components/AdminRiderManagement/ApproveDialog.vue";
import RejectDialog from "@/components/AdminRiderManagement/RejectDialog.vue";
import { getRiderListApi, approveRiderApi, rejectRiderApi, updateRiderStatusApi } from "@/services/api/riderServices";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "AdminRiderManagement",

  components: {
    RiderCard,
    Select,
    Table,
    ApproveDialog,
    RejectDialog,
  },

  data() {
    return {
      isLoading: false,
      riders: [],
      selectedCardIndex: 0,
      search: "",
      selectedStatus: "all",
      dateRange: [],
      showApproveDialog: false,
      showRejectDialog: false,
      selectedRider: null,
      approving: false,
      rejecting: false,
    };
  },

  async created() {
    await this.loadRiders();
  },

  computed: {
    filteredRiders() {
      let result = [...this.riders];

      const normalizeStatus = (status) => {
        const v = (status || "").toString().trim().toLowerCase();
        if (v === "pending_approval") return "pending_approval";
        if (v === "active") return "active";
        if (v === "inactive") return "inactive";
        if (v === "rejected") return "rejected";
        if (["suspended", "ban", "banned"].includes(v)) return "suspended";
        return "unknown";
      };

      const cardStatusMap = {
        1: "pending_approval",
        2: "active",
        3: "suspended",
      };
      const cardFilter = cardStatusMap[this.selectedCardIndex];
      if (cardFilter) {
        result = result.filter((r) => normalizeStatus(r.status) === cardFilter);
      }

      if (this.selectedStatus && this.selectedStatus !== "all") {
        result = result.filter(
          (r) => normalizeStatus(r.status) === this.selectedStatus
        );
      }

      if ((this.search || "").trim()) {
        const kw = this.search.toLowerCase().trim();

        result = result.filter((s) => {
          const first = (s.natId_firstName || "").trim();
          const last = (s.natId_lastName || "").trim();
          const fullName = [first, last].filter(Boolean).join(" ").toLowerCase();
          const email = (s.email || "").toLowerCase();
          const phone = (s.phone || "").toLowerCase();
          const username = (s.username || "").toLowerCase();
          
          return (
            fullName.includes(kw) ||
            email.includes(kw) ||
            phone.includes(kw) ||
            username.includes(kw)
          );
        });
      }

      if (this.dateRange && this.dateRange.length === 2) {
        const [startRaw, endRaw] = this.dateRange;
        const startStr = new Date(startRaw).toISOString().slice(0, 10);
        const endStr = new Date(endRaw).toISOString().slice(0, 10);
        const [minStr, maxStr] = startStr <= endStr ? [startStr, endStr] : [endStr, startStr];

        result = result.filter((s) => {
          if (!s.createdAt) return false;
          const created = new Date(s.createdAt);
          if (isNaN(created)) return false;
          const createdStr = created.toISOString().slice(0, 10);
          return createdStr >= minStr && createdStr <= maxStr;
        });
      }

      return result;
    },
  },

  methods: {
    async loadRiders() {
      this.isLoading = true;
      try {
        const data = await getRiderListApi();
        if (data && Array.isArray(data.data)) {
          this.riders = data.data;
        } else if (data && Array.isArray(data.riders)) {
          this.riders = data.riders;
        } else if (Array.isArray(data)) {
          this.riders = data;
        } else {
          this.riders = [];
        }
      } catch (err) {
        console.error("[AdminRiderManagement] loadRiders error:", err);
        this.riders = [];
      } finally {
        this.isLoading = false;
      }
    },

    onSelectCard(index) {
      this.selectedCardIndex = index;
    },

    openRiderDetail(rider) {
      this.$router.push({ name: "admin_rider_detail", params: { riderId: rider.id } });
    },

    openApproveDialog(rider) {
      this.selectedRider = rider;
      this.showApproveDialog = true;
    },

    openRejectDialog(rider) {
      this.selectedRider = rider;
      this.showRejectDialog = true;
    },

    async handleApprove({ vehicleServices }) {
      this.approving = true;
      try {
        await approveRiderApi(this.selectedRider.id, { vehicleServices });
        this.showApproveDialog = false;
        swalAlert.Success("อนุมัติไรเดอร์สำเร็จ");
        await this.loadRiders();
      } catch (err) {
        console.error("handleApprove error:", err);
        const msg = err?.response?.data?.message || "เกิดข้อผิดพลาดในการอนุมัติ";
        swalAlert.Error(msg);
      } finally {
        this.approving = false;
      }
    },

    async handleReject({ rejectReason }) {
      this.rejecting = true;
      try {
        await rejectRiderApi(this.selectedRider.id, { rejectReason });
        this.showRejectDialog = false;
        swalAlert.Success("ปฏิเสธไรเดอร์สำเร็จ");
        await this.loadRiders();
      } catch (err) {
        console.error("handleReject error:", err);
        const msg = err?.response?.data?.message || "เกิดข้อผิดพลาดในการปฏิเสธ";
        swalAlert.Error(msg);
      } finally {
        this.rejecting = false;
      }
    },

    async toggleSuspendRider(rider) {
      const currentStatus = (rider.status || "").toLowerCase();
      const isSuspended = ["suspended", "ban", "banned"].includes(currentStatus);
      const newStatus = isSuspended ? "active" : "suspended";
      const actionText = isSuspended ? "ยกเลิกระงับ" : "ระงับ";

      const result = await swalAlert.Confirm({
        title: `ยืนยันการ${actionText}`,
        message: `คุณต้องการ${actionText}บัญชี "${rider.email}" ใช่หรือไม่?`,
      });

      if (result.isConfirmed) {
        this.isLoading = true;
        try {
          await updateRiderStatusApi(rider.id, newStatus);
          swalAlert.Success("ดำเนินการสำเร็จ");
          await this.loadRiders();
        } catch (error) {
          console.error("toggleSuspendRider error:", error);
          swalAlert.Error("เกิดข้อผิดพลาด");
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.title-text {
  font-size: 24px;
  font-weight: 700;
  color: #1f2a39;
}
.no-data-text {
  font-size: 14px;
  font-weight: 400;
  color: #6b717f;
}
.card-container {
  border-radius: 16px !important;
  box-shadow: 0 0 2px 0 #1018280d !important;
}

@media (max-width: 960px) {
  .create-btn {
    width: 100% !important;
  }
}
</style>
